import React, { useEffect, useId, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { CLUB_ACCOUNT_TERMS, publicHowBracketsWork, publicAgeDivisionGuide, publicAgeDivisionConfirm } from '../constants/tournaments';

const toneClasses = {
  light: {
    card: 'rounded-xl border border-slate-200 bg-white/90 text-slate-800',
    muted: 'text-slate-600',
    bullet: 'text-slate-800',
    btn: 'text-[#1e4b85] hover:bg-slate-100',
  },
  dark: {
    card: 'rounded-xl border border-white/15 bg-white/10 text-white',
    muted: 'text-white/75',
    bullet: 'text-white',
    btn: 'text-white hover:bg-white/10',
  },
  public: {
    card: 'rounded-xl border border-white/15 bg-[#0f172a]/80 text-slate-100',
    muted: 'text-slate-300',
    bullet: 'text-slate-100',
    btn: 'text-teal-200 hover:bg-white/10',
  },
};

export const getHowItWorksCopy = (mode, tournament) => {
  if (mode === 'account') return CLUB_ACCOUNT_TERMS;
  if (mode === 'age-division') return publicAgeDivisionGuide();
  return publicHowBracketsWork(tournament);
};

export function HowItWorksBody({ copy }) {
  return (
    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
      <ul className="list-disc space-y-1.5 rounded-lg bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-800">
        {copy.bullets.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {copy.sections.map((section) => (
        <div key={section.heading}>
          <h4 className="text-xs font-extrabold uppercase tracking-widest text-slate-500">
            {section.heading}
          </h4>
          <p className="mt-1 text-sm leading-relaxed text-slate-700">{section.body}</p>
        </div>
      ))}
    </div>
  );
}

/** Radix Dialog/Presence + React 19 white-screens (#185) when stacked on the wizard portal. */
export function PortalDialog({
  open,
  onOpenChange,
  title,
  overlayClassName = '',
  contentClassName = '',
  maxClassName = 'max-h-[min(92dvh,720px)] max-w-lg',
  children,
  footer,
}) {
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onOpenChange?.(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4 ${overlayClassName}`}
      role="presentation"
      onClick={() => onOpenChange?.(false)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`relative flex w-full ${maxClassName} flex-col overflow-hidden rounded-lg border bg-background shadow-lg ${contentClassName}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="shrink-0 border-b px-4 py-4 pr-12 text-left sm:px-6">
          <h2 id={titleId} className="text-lg font-semibold leading-none tracking-tight">
            {title}
          </h2>
        </div>
        <button
          type="button"
          className="absolute right-4 top-4 z-[60] inline-flex h-9 w-9 items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          onClick={() => onOpenChange?.(false)}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        {children}
        {footer}
      </div>
    </div>,
    document.body,
  );
}

export function HowBracketsWorkTldr({
  tournament = null,
  mode = 'tournament',
  variant = 'light',
  onOpenFull,
  className = '',
}) {
  const copy = useMemo(
    () => getHowItWorksCopy(mode, tournament),
    [mode, tournament],
  );
  const tone = toneClasses[variant] || toneClasses.light;

  return (
    <div className={`${tone.card} p-3 ${className}`}>
      <div className="mb-2 flex items-start justify-between gap-2">
        <p className={`text-[10px] font-extrabold uppercase tracking-widest ${tone.muted}`}>
          TLDR
        </p>
        <button
          type="button"
          onClick={onOpenFull}
          className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${tone.btn}`}
          title="Read full details"
          aria-label="How brackets work"
        >
          <HelpCircle className="h-4 w-4" />
        </button>
      </div>
      <p className={`text-sm font-bold leading-snug ${tone.bullet}`}>{copy.title}</p>
      <ul className={`mt-2 list-disc space-y-1.5 pl-4 text-xs leading-relaxed ${tone.muted}`}>
        {copy.bullets.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onOpenFull}
        className={`mt-2 text-[11px] font-semibold underline-offset-2 hover:underline ${tone.btn}`}
      >
        Read full details
      </button>
    </div>
  );
}

export default function TournamentHowItWorksDialog({
  open,
  onOpenChange,
  tournament = null,
  mode = 'tournament',
  requireAck = false,
  onAck,
  overlayClassName,
  contentClassName,
}) {
  const copy = useMemo(
    () => getHowItWorksCopy(mode, tournament),
    [mode, tournament],
  );

  const acknowledge = () => {
    onAck?.();
    onOpenChange?.(false);
  };

  return (
    <PortalDialog
      open={open}
      onOpenChange={onOpenChange}
      title={copy.title}
      overlayClassName={overlayClassName}
      contentClassName={contentClassName}
      footer={(
        <div className="shrink-0 border-t bg-background px-4 py-3 sm:flex sm:flex-row sm:justify-end sm:px-6">
          {requireAck ? (
            <Button type="button" className="btn-primary" onClick={acknowledge}>
              I have read this
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)}>
              Close
            </Button>
          )}
        </div>
      )}
    >
      <HowItWorksBody copy={copy} />
    </PortalDialog>
  );
}

export function AgeDivisionTldr({
  tournament = null,
  variant = 'light',
  onOpenFull,
  className = '',
}) {
  const copy = useMemo(() => publicAgeDivisionConfirm(tournament), [tournament]);
  const tone = toneClasses[variant] || toneClasses.light;
  return (
    <div className={`${tone.card} p-3 ${className}`}>
      <div className="mb-2 flex items-start justify-between gap-2">
        <p className={`text-[10px] font-extrabold uppercase tracking-widest ${tone.muted}`}>
          Age division
        </p>
        {onOpenFull ? (
          <button
            type="button"
            onClick={onOpenFull}
            className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${tone.btn}`}
            title="Age divisions"
            aria-label="Age divisions"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <p className={`text-sm font-bold leading-snug ${tone.bullet}`}>{copy.headline}</p>
      <p className={`mt-1.5 text-xs leading-relaxed ${tone.muted}`}>{copy.body}</p>
      {onOpenFull ? (
        <button
          type="button"
          onClick={onOpenFull}
          className={`mt-2 text-[11px] font-semibold underline-offset-2 hover:underline ${tone.btn}`}
        >
          What Open / Junior / Seniors means
        </button>
      ) : null}
    </div>
  );
}

export function AgeDivisionConfirmDialog({
  open,
  onOpenChange,
  tournament = null,
  onConfirm,
}) {
  const copy = useMemo(() => publicAgeDivisionConfirm(tournament), [tournament]);

  const continueRegister = () => {
    onConfirm?.();
    onOpenChange?.(false);
  };

  return (
    <PortalDialog
      open={open}
      onOpenChange={onOpenChange}
      title={copy.title}
      maxClassName="max-h-[min(92dvh,560px)] max-w-md"
      footer={(
        <div className="flex shrink-0 flex-col-reverse gap-2 border-t bg-background px-4 py-3 sm:flex-row sm:justify-end sm:px-6">
          <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)}>
            Stay here
          </Button>
          <Button type="button" className="btn-primary" onClick={continueRegister}>
            {copy.confirmLabel}
          </Button>
        </div>
      )}
    >
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
        <p className="text-base font-bold text-slate-900">{copy.headline}</p>
        <p className="text-sm leading-relaxed text-slate-700">{copy.body}</p>
        <p className="text-xs leading-relaxed text-slate-500">
          You can still switch Age Division pills to All, Open, Kids / Junior, or Seniors after this.
        </p>
      </div>
    </PortalDialog>
  );
}
